import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, MessageCircle, PencilLine, Send, ShieldCheck } from "lucide-react";
import { useLang } from "../lib/lang";
import { WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnquiryForm() {
  const { lang, t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", phone: "", cls: "", need: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("form");

  const normalized = useMemo(() => {
    const d = form.phone.replace(/\D/g, "");
    if (d.length === 12 && d.startsWith("91")) return d.slice(2);
    if (d.length === 11 && d.startsWith("0")) return d.slice(1);
    return d;
  }, [form.phone]);

  const message = useMemo(() => {
    const m = c.msg;
    return `${m.greet}\n\n${m.intro}\n\n• ${m.parent}: ${form.name.trim()}\n• ${m.cls}: ${form.cls}\n• ${m.need}: ${form.need}\n\n${m.close}\n\n${m.footer}`;
  }, [c, form]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = c.errors.name;
    if (!/^[6-9]\d{9}$/.test(normalized)) e.phone = c.errors.phone;
    if (!form.cls) e.cls = c.errors.cls;
    if (!form.need) e.need = c.errors.need;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          parent_name: form.name.trim(),
          whatsapp_number: normalized,
          student_class: form.cls,
          learning_need: form.need,
          lang,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error("save-failed");
      setStep("review");
    } catch {
      setStep("review");
    } finally {
      setBusy(false);
    }
  };

  const openWhatsApp = () => {
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
    setStep("done");
  };

  const set = (k) => (ev) => setForm((f) => ({ ...f, [k]: ev.target.value }));
  const hiFont = lang === "hi" ? "font-hi" : "";

  if (step === "review" || step === "done") {
    return (
      <div className="card-warm p-6 sm:p-8" data-testid="enquiry-review-card">
        <AnimatePresence mode="wait">
          {step === "review" ? (
            <motion.div key="review" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <h3 className="font-display text-2xl text-ink" data-testid="review-title">{c.review.title}</h3>
              <div
                className="mt-5 whitespace-pre-line rounded-2xl border border-[#E5DECF] bg-[#FDFBF7] p-5 text-[15px] leading-relaxed text-[#0F172A]"
                data-testid="review-message-preview"
              >
                {message}
              </div>
              <p
                className="mt-5 flex items-start gap-2.5 rounded-2xl bg-[#FFEDD5]/70 p-4 text-sm leading-relaxed text-[#7C2D12]"
                data-testid="review-explanation"
              >
                <Send size={17} className="mt-0.5 shrink-0 text-saffron" />
                {c.review.explain}
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button onClick={openWhatsApp} data-testid="review-open-whatsapp-button" className="btn-saffron flex-1">
                  <MessageCircle size={17} />
                  {c.review.open}
                </button>
                <button
                  onClick={() => setStep("form")}
                  data-testid="review-edit-button"
                  className="btn-outline flex-1"
                >
                  <PencilLine size={16} />
                  {c.review.edit}
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div key="done" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center">
              <CheckCircle2 size={48} className="mx-auto text-saffron" />
              <h3 className="mt-4 font-display text-2xl text-ink" data-testid="review-done-title">{c.review.done}</h3>
              <p className={`mt-3 text-[15px] leading-relaxed text-[#475569] ${hiFont}`} data-testid="review-done-text">
                {c.review.doneText}
              </p>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="review-direct-whatsapp-link"
                className="mt-5 inline-flex items-center gap-2 font-semibold text-saffron underline decoration-2 underline-offset-4 hover:text-ink"
              >
                <MessageCircle size={17} />
                {WHATSAPP_DISPLAY}
              </a>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="card-warm p-6 sm:p-8" data-testid="enquiry-form">
      <div className="space-y-5">
        <div>
          <label htmlFor="enq-name" className={`field-label ${hiFont}`}>{c.labels.name}</label>
          <input
            id="enq-name"
            type="text"
            autoComplete="name"
            value={form.name}
            onChange={set("name")}
            placeholder={c.labels.namePh}
            className={`field ${hiFont}`}
            data-testid="enquiry-name-input"
            aria-invalid={!!errors.name}
          />
          {errors.name && <p className="mt-1.5 text-sm font-medium text-[#DC2626]" data-testid="enquiry-name-error">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="enq-phone" className={`field-label ${hiFont}`}>{c.labels.phone}</label>
          <div className="flex items-stretch overflow-hidden rounded-2xl border border-[#E5DECF] bg-white/90 transition-all focus-within:border-[#EA580C] focus-within:ring-4 focus-within:ring-[#EA580C]/15">
            <span className="flex items-center border-r border-[#E5DECF] bg-cream px-4 text-sm font-bold text-ink">+91</span>
            <input
              id="enq-phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder="99083 10130"
              className="w-full bg-transparent px-4 py-3.5 text-[15px] outline-none placeholder:text-[#64748B]/60"
              data-testid="enquiry-phone-input"
              aria-invalid={!!errors.phone}
            />
          </div>
          <p className={`mt-1.5 text-xs text-[#64748B] ${hiFont}`}>{c.labels.phoneHint}</p>
          {errors.phone && <p className="mt-1 text-sm font-medium text-[#DC2626]" data-testid="enquiry-phone-error">{errors.phone}</p>}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="enq-class" className={`field-label ${hiFont}`}>{c.labels.cls}</label>
            <select
              id="enq-class"
              value={form.cls}
              onChange={set("cls")}
              className={`field ${form.cls ? "" : "text-[#64748B]/70"} ${hiFont}`}
              data-testid="enquiry-class-select"
              aria-invalid={!!errors.cls}
            >
              <option value="" disabled>{c.labels.needPh}</option>
              {c.classOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            {errors.cls && <p className="mt-1.5 text-sm font-medium text-[#DC2626]" data-testid="enquiry-class-error">{errors.cls}</p>}
          </div>
          <div>
            <label htmlFor="enq-need" className={`field-label ${hiFont}`}>{c.labels.need}</label>
            <select
              id="enq-need"
              value={form.need}
              onChange={set("need")}
              className={`field ${form.need ? "" : "text-[#64748B]/70"} ${hiFont}`}
              data-testid="enquiry-need-select"
              aria-invalid={!!errors.need}
            >
              <option value="" disabled>{c.labels.needPh}</option>
              {c.needOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            {errors.need && <p className="mt-1.5 text-sm font-medium text-[#DC2626]" data-testid="enquiry-need-error">{errors.need}</p>}
          </div>
        </div>

        <button type="submit" disabled={busy} data-testid="enquiry-submit-button" className="btn-primary w-full disabled:opacity-60">
          <MessageCircle size={17} />
          {busy ? c.submitting : c.submit}
        </button>

        <p className={`flex items-start gap-2 text-xs leading-relaxed text-[#64748B] ${hiFont}`} data-testid="enquiry-privacy-micro">
          <ShieldCheck size={15} className="mt-0.5 shrink-0 text-saffron" />
          {t.footer.privacyLine}
        </p>
      </div>
    </form>
  );
}
